<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse
        v-for="(route, index) in navigationRoutes.routes"
        :key="index"
        :header-padding="false"
        :model-value="activeStates[index]"
        @update:modelValue="(val) => updateActiveState(index, val)"
      >
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ t(route.displayName) }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(childRoute.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <VaIcon
                  v-if="childRoute.meta && childRoute.meta.icon"
                  aria-hidden="true"
                  :name="childRoute.meta.icon"
                  size="18px"
                  :color="iconColor(childRoute)"
                  class="mr-2"
                />
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()

    const value = ref<boolean[]>([])
    const activeStates = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.path.endsWith(`${section.name}`)
      }

      return section.children.some(({ name }) => route.path.endsWith(`${name}`))
    }

    const setActiveExpand = () => {
      // 保存系统管理菜单的当前状态
      const managementIndex = navigationRoutes.routes.findIndex((route) => route.name === 'management')
      const managementExpanded =
        managementIndex !== -1 && value.value && value.value.length > managementIndex
          ? value.value[managementIndex]
          : false

      // 确保 value.value 是一个数组
      if (!Array.isArray(value.value)) {
        value.value = []
      }

      // 同步初始化 activeStates
      if (!Array.isArray(activeStates.value) || activeStates.value.length !== navigationRoutes.routes.length) {
        activeStates.value = navigationRoutes.routes.map(() => false)
      }

      // 根据当前路由设置菜单展开状态
      value.value = navigationRoutes.routes.map((route: INavigationRoute, index) => {
        // 如果是系统管理菜单且之前是展开的，则保持展开状态
        const isActive = route.name === 'management' && managementExpanded ? true : routeHasActiveChild(route)
        // 同步更新 activeStates
        activeStates.value[index] = isActive
        return isActive
      })
    }

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    const updateActiveState = (index: number, val: boolean) => {
      activeStates.value[index] = val
    }

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    onMounted(() => {
      setActiveExpand()
      // 确保系统管理菜单默认展开
      const managementIndex = navigationRoutes.routes.findIndex((route) => route.name === 'management')
      if (
        managementIndex !== -1 &&
        Array.isArray(value.value) &&
        managementIndex < value.value.length &&
        !value.value[managementIndex]
      ) {
        value.value[managementIndex] = true
        // 同步更新 activeStates
        if (Array.isArray(activeStates.value) && managementIndex < activeStates.value.length) {
          activeStates.value[managementIndex] = true
        }
      }
    })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
      activeStates,
      updateActiveState,
    }
  },
})
</script>

<style lang="scss" scoped>
:deep(.va-collapse) {
  border: none;
  box-shadow: none;
}

:deep(.va-collapse__body) {
  padding: 0;
}

:deep(.va-collapse__header) {
  padding: 0;
  border: none;
}
</style>
